/**
 * =============================================================================
 * STOX Theme - Base JavaScript
 * =============================================================================
 * 
 * This file contains site-wide behaviors and shared utilities.
 * 
 * FILE OWNERSHIP:
 * - Shared helpers: onReady, bindOnce, loadScriptOnce, addToCart, updateCart
 * - Site-wide components: header, cart drawer, mobile menu, search, accordion
 * - Theme Editor lifecycle hooks
 * 
 * MOUNT/UNMOUNT:
 * - window.app.mount() is called on initial load
 * - shopify:section:load => mount(sectionRoot)
 * - shopify:section:unload => unmount(sectionRoot)
 * 
 * IMPORTANT: All standalone handlers below use idempotent patterns to prevent
 * duplicate bindings on section reload.
 * =============================================================================
 */

(() => {
  "use strict";

  // ==========================================================================
  // SHARED HELPERS - Available via window.Stox namespace
  // ==========================================================================

  // WeakMap to track bound event listeners (prevents duplicate bindings)
  const boundListeners = new WeakMap();

  /**
   * Safely run a function after DOM is ready.
   * If DOM is already ready, runs immediately.
   * @param {Function} fn - Function to run
   */
  const onReady = (fn) => {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn, { once: true });
    } else {
      fn();
    }
  };

  /**
   * Bind an event listener only once per element+key combination.
   * Uses WeakMap to track bindings without leaking memory.
   * @param {Element} el - DOM element
   * @param {string} key - Unique key for this binding
   * @param {string} event - Event name
   * @param {Function} handler - Event handler
   * @param {Object} options - Event listener options
   * @returns {boolean} - True if newly bound, false if already bound
   */
  const bindOnce = (el, key, event, handler, options = {}) => {
    if (!el) return false;
    
    let elBindings = boundListeners.get(el);
    if (!elBindings) {
      elBindings = new Set();
      boundListeners.set(el, elBindings);
    }
    
    const bindingKey = `${event}:${key}`;
    if (elBindings.has(bindingKey)) return false;
    
    elBindings.add(bindingKey);
    el.addEventListener(event, handler, options);
    return true;
  };

  /**
   * Remove a bound listener and clear its tracking.
   * @param {Element} el - DOM element
   * @param {string} key - Unique key used when binding
   * @param {string} event - Event name
   * @param {Function} handler - Event handler
   * @param {Object} options - Event listener options
   */
  const unbind = (el, key, event, handler, options = {}) => {
    if (!el) return;
    
    const elBindings = boundListeners.get(el);
    if (elBindings) {
      elBindings.delete(`${event}:${key}`);
    }
    el.removeEventListener(event, handler, options);
  };

  /**
   * Load an external script only once.
   * Checks both tracking object and existing script tags.
   * @param {string} src - Script URL
   * @param {Function} callback - Optional callback on load
   * @returns {Promise} - Resolves when script is loaded
   */
  const loadScriptOnce = (src, callback) => {
    return new Promise((resolve) => {
      // Check if already tracked as loaded
      if (window.hasLoaded?.scripts?.[src]) {
        callback?.();
        resolve();
        return;
      }
      
      // Check if script tag already exists
      if (document.querySelector(`script[src="${src}"]`)) {
        window.hasLoaded = window.hasLoaded || { scripts: {}, styles: {} };
        window.hasLoaded.scripts[src] = true;
        callback?.();
        resolve();
        return;
      }
      
      // Create and insert script
      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      script.addEventListener("load", () => {
        window.hasLoaded = window.hasLoaded || { scripts: {}, styles: {} };
        window.hasLoaded.scripts[src] = true;
        callback?.();
        resolve();
      });
      document.head.appendChild(script);
    });
  };

  /**
   * Debounce a function call.
   * @param {Function} fn - Function to debounce
   * @param {number} wait - Wait time in ms
   * @returns {Function} - Debounced function
   */
  const debounce = (fn, wait) => {
    let timeout;
    return function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(context, args), wait);
    };
  };

  /**
   * Throttle a function using requestAnimationFrame.
   * @param {Function} fn - Function to throttle
   * @returns {Function} - Throttled function
   */
  const throttleRAF = (fn) => {
    let ticking = false;
    return function (...args) {
      const context = this;
      if (!ticking) {
        window.requestAnimationFrame(() => {
          fn.apply(context, args);
          ticking = false;
        });
        ticking = true;
      }
    };
  };

  // Expose helpers on window.Stox namespace
  window.Stox = window.Stox || {};
  Object.assign(window.Stox, {
    onReady,
    bindOnce,
    unbind,
    loadScriptOnce,
    debounce,
    throttleRAF,
  });

  // ==========================================================================
  // THEME EDITOR LIFECYCLE HOOKS (attached once)
  // ==========================================================================

  // Track if Theme Editor hooks are already attached
  if (!window.__themeEditorHooksAttached) {
    window.__themeEditorHooksAttached = true;

    document.addEventListener("shopify:section:load", (event) => {
      // Re-mount components within the loaded section
      window.app?.unmount?.();
      window.app?.mount?.();
    });

    document.addEventListener("shopify:section:unload", (event) => {
      // Cleanup is handled by app.unmount() which is called before section is removed
      window.app?.unmount?.();
    });

    document.addEventListener("shopify:section:select", () => {
      // Section selected in editor - ensure mounted
      window.app?.mount?.();
    });
  }

  // ==========================================================================
  // DOM ELEMENT FINDER UTILITY
  // ==========================================================================

  function e(e = document.body, t = "js-") {
    const n = [...e.querySelectorAll(t ? `[class*="${t}"]` : "*")],
      o = t ? "classList" : "dataset";
    return n.reduce(
      (e, n) => (
        [].slice.call(t ? n[o] : Object.entries(n[o])).forEach((o) => {
          let a;
          t && o.slice(0, t.length) === t
            ? (a = o.slice(t.length, o.length))
            : t || ([a] = o),
            a &&
              (e[a] = e.hasOwnProperty(a)
                ? e[a].constructor === Array
                  ? e[a].concat(n)
                  : [e[a], n]
                : n);
        }),
        e
      ),
      {}
    );
  }

  // ==========================================================================
  // SHARED CART FUNCTIONS
  // ==========================================================================

  const isCartPage = window.location.href.includes(window.Stox?.routes?.cart || '/cart');

  /**
   * Add items to cart with section rendering support.
   * Used by both base.js components and product.js.
   * @param {Array} items - Array of { id, quantity, properties? }
   * @returns {Promise} - Resolves with { item, cart, error? }
   */
  function addToCart(items = []) {
    window.app?.emit?.("cart:updating");
    
    return fetch(`${window.Stox?.routes?.cartAdd || '/cart/add'}.js`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items }),
    })
      .then((res) => res.json())
      .then(async (data) => {
        if (data.status) {
          // Error response
          window.app?.emit?.(["cart:updated", "cart:error"], null, data);
          return {
            item: data,
            cart: window.app?.getState?.()?.cart,
            error: data?.description,
          };
        }

        // Fetch updated cart with sections
        const sectionId = isCartPage ? "main-cart" : "cart-drawer";
        const [cart, sections] = await Promise.all([
          fetch(`${window.Stox?.routes?.cart || '/cart'}.js`, {
            method: "GET",
            credentials: "include",
          }).then((r) => r.json()),
          fetch(`${window.Stox?.routes?.cart || '/cart'}?sections=${sectionId}`, {
            method: "GET",
            credentials: "include",
          }).then((r) => r.json()),
        ]);

        cart.sections = sections;
        window.app?.emit?.(["cart:updated", "cart:render"], { cart });

        // Set Invoice Language once per session
        const shopLocale = document.body.getAttribute("data-locale");
        const sessionLocale = sessionStorage.getItem("invoice_language");

        if (!window._invoiceLangChecked && shopLocale && sessionLocale !== shopLocale) {
          if (cart.attributes?.["Invoice Language"] !== shopLocale) {
            await fetch("/cart/update.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                attributes: { "Invoice Language": shopLocale },
              }),
            });
          }
          sessionStorage.setItem("invoice_language", shopLocale);
          window._invoiceLangChecked = true;
        }

        return { item: data, cart };
      });
  }

  /**
   * The express checkout divider should only appear when Shopify actually
   * renders wallet buttons. `additional_checkout_buttons` is true server-side
   * whenever the store has offsite providers, but a given device/browser may
   * not support any of them (e.g. Apple Pay off non-Apple devices), leaving the
   * wallet container empty. We detect that client-side by measuring the host
   * height and toggle a "ready" class so the divider hides and the regular
   * payment icons stay visible when no buttons render.
   */
  function updateExpressCheckout(root = document) {
    const wrappers = root.querySelectorAll?.("[data-express-checkout]");
    if (!wrappers || !wrappers.length) return;

    wrappers.forEach((wrapper) => {
      const wallet = wrapper.querySelector("shopify-accelerated-checkout-cart");
      if (!wallet) return;

      const update = () => {
        const ready = wallet.getBoundingClientRect().height > 8;
        wrapper.classList.toggle("cart-express-checkout--ready", ready);
      };

      update();
      [100, 300, 800, 1500].forEach((delay) => window.setTimeout(update, delay));

      if (window.ResizeObserver && !wallet.__expressObserved) {
        wallet.__expressObserved = true;
        new ResizeObserver(update).observe(wallet);
      }
    });
  }

  /**
   * Accelerated checkout buttons only render when the section is requested
   * from the cart route, not when the drawer is rendered on other templates.
   */
  function fetchCartDrawerSection() {
    return Promise.all([
      fetch(`${window.Stox?.routes?.cart || '/cart'}.js`, {
        method: "GET",
        credentials: "include",
      }).then((r) => r.json()),
      fetch(`${window.Stox?.routes?.cart || '/cart'}?sections=cart-drawer`, {
        method: "GET",
        credentials: "include",
      }).then((r) => r.json()),
    ]).then(([cart, sections]) => {
      cart.sections = sections;
      return cart;
    });
  }

  /**
   * @param {string} id - Line item key
   * @param {number} quantity - New quantity
   * @returns {Promise} - Resolves with cart object
   */
  function updateCartItem(id, quantity) {
    window.app?.emit?.("cart:updating");
    
    const sectionId = isCartPage ? "main-cart" : "cart-drawer";
    
    return fetch(`${window.Stox?.routes?.cartChange || '/cart/change'}.js`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id, quantity, sections: sectionId }),
    })
      .then((res) => res.json())
      .then((cart) => {
        window.app?.emit?.(["cart:updated", "cart:render"], { cart });
        return cart;
      });
  }

  /**
   * Clear entire cart.
   * @returns {Promise} - Resolves with cart object
   */
  function clearCart() {
    window.app?.emit?.("cart:updating");
    
    const sectionId = isCartPage ? "main-cart" : "cart-drawer";
    
    return fetch(`${window.Stox?.routes?.cartClear || '/cart/clear'}.js`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sections: sectionId }),
    })
      .then((res) => res.json())
      .then((cart) => {
        window.app?.emit?.(["cart:updated", "cart:render"], { cart });
        return cart;
      });
  }

  // Expose cart functions for use by other files
  window.Stox.addToCart = addToCart;
  window.Stox.updateCartItem = updateCartItem;
  window.Stox.clearCart = clearCart;

  // ==========================================================================
  // ESCAPE KEY HANDLER UTILITY
  // ==========================================================================

  const handleEscapeKey = ({ keyCode }, { type, boolean }) => {
    if (keyCode === 27 && type && boolean) {
      window.app?.emit?.(type, { [boolean]: false });
    }
  };

  // ==========================================================================
  // COMPONENT DEFINITIONS
  // ==========================================================================

  const t = window.component(async (t) => {}); // newsletterValidation placeholder

  const n = (e) =>
    new Promise((t) => {
      window?.hasLoaded?.scripts?.[e] ? t() : window.app?.on?.(`${e}:loaded`, t);
    });

  const o = window.component(async (t) => {}); // videoGrid placeholder

  const a = window.component((t) => {
    const { quote: n, quoteText: o, container: a } = e(t, null);
    if (!n || !a) return; // Defensive check
    
    const r = [].concat(n),
      s = [].concat(o);
    let i,
      c = 0,
      l = 0;
    
    const d = () => {
      a.scrollTo({ behavior: "smooth", left: l });
      r.forEach((e, t) => {
        e.classList.remove("filter-none");
        if (c === t) e.classList.add("filter-none");
      });
      c === r.length - 1 ? ((l = 0), (c = 0)) : ((l = 200 * (c + 1)), ++c);
    };
    
    const p = () => {
      i = setInterval(() => {
        r.length > 0 ? d() : clearInterval(i);
      }, 5e3);
    };
    
    r.forEach((e, t) => {
      e.addEventListener("click", () => {
        clearInterval(i);
        l = 200 * t;
        c = t;
        d();
        p();
      });
    });
    
    d();
    p();
    
    // Return cleanup function
    return () => clearInterval(i);
  });

  const r = {
    newsletterValidation: t,
    textAnimation: window.component((t, n) => {
      const { animatedList: o } = e(t, null);
      if (!o) return; // Defensive check
      
      // Generate unique key for this instance to allow multiple textAnimation components
      const instanceKey = `textAnimation-${t.id || Math.random().toString(36).substr(2, 9)}`;
      
      const handleScroll = throttleRAF(() => {
        const windowHeight = window.innerHeight / 2;
        const elHeight = t.offsetHeight;
        const elTop = t.getBoundingClientRect().top + elHeight / 2;
        o.style.transform = `translate3d(${-1 * (windowHeight - elTop)}px,0,0)`;
      });
      
      // Use bindOnce to prevent duplicate scroll handlers
      bindOnce(window, instanceKey, "scroll", handleScroll, { passive: true });
      
      return () => unbind(window, instanceKey, "scroll", handleScroll, { passive: true });
    }),
    videoGrid: o,
    quotes: a,
    mainBlog: window.component((e, t) => {
      const n = e;
      if (!n?.dataset?.blogCount) return; // Defensive check
      
      let o,
        a = 1;
      
      if (e.dataset.blogCount >= 6) {
        o = {
          articleGrid: "[data-article-grid]",
          button: "[data-load-more-articles]",
        };
        
        const button = n.querySelector(o.button);
        if (!button) return;
        
        button.addEventListener("click", async () => {
          a++;
          const htmlDoc = await fetch(
            `${window.location.origin + window.location.pathname}?page=${a}`
          )
            .then((res) => res.text())
            .then((text) => new DOMParser().parseFromString(text, "text/html"));
          
          if (Math.ceil(e.dataset.blogCount / 6) === a) {
            button.remove();
          }
          
          window.app?.emit?.(["articles:render"], { html: htmlDoc });
        });
        
        t.on("articles:render", ({ html }) => {
          const grid = n.querySelector(o.articleGrid);
          const newContent = html?.querySelector(o.articleGrid);
          if (grid && newContent) {
            grid.innerHTML += newContent.innerHTML;
          }
        });
      }
    }),
  };

  const s = window.component(() => {
    // This component is now deprecated - Theme Editor hooks are handled globally above
    // Keeping for backwards compatibility but it does nothing
  });

  const i = window.component((e) => {
    const { text: t } = e.dataset;
    console.log("console:log", t);
  });

  // Header scroll thresholds
  const SCROLL_THRESHOLD = 86;
  const TOP_OFFSET = 30;

  const c = window.component((t, n) => {
    const { group: o, timer: a, index: r } = t.dataset;
    const { inner: s, toggle: i } = e(t, null);
    if (!s || !i) return; // Defensive check
    
    const openClass = "open";
    let isOpen = false;

    const updateHeight = () => {
      t.style.setProperty(
        "--innerHeight",
        s.scrollHeight > 0 ? `${s.scrollHeight}px` : "auto"
      );
    };
    
    updateHeight();

    const timerAccordions = Array.from(
      document.querySelectorAll(`[data-group="${o}"][data-timer]`)
    );

    const isInView = () => {
      if (timerAccordions.length === 0) return true;
      const firstTimer = timerAccordions.find((e) => e.dataset.index === "0") || timerAccordions[0];
      return firstTimer?.dataset?.inView === "true";
    };

    // Debounced resize handler with bindOnce
    const handleResize = debounce(updateHeight, 150);
    bindOnce(window, `accordion-${o}-${r}`, "resize", handleResize, { passive: true });

    i.addEventListener("click", () => {
      const willOpen = !t.classList.contains(openClass);
      
      // Scroll anchoring: when switching accordions, ensure the clicked header
      // is visible after all transitions complete.
      if (willOpen && o) {
        const openAccordion = document.querySelector(
          `[data-component="accordion"][data-group="${o}"].${openClass}`
        );
        
        // If there's another accordion open in this group
        if (openAccordion && openAccordion !== t) {
          // Get sticky header height for offset calculation
          const stickyHeader = document.querySelector("[data-header]");
          const stickyHeaderHeight = stickyHeader ? stickyHeader.offsetHeight : 0;
          
          // After transitions complete, scroll header into view
          setTimeout(() => {
            const headerRect = t.getBoundingClientRect();
            
            // Only scroll if header is not properly visible (above sticky header)
            if (headerRect.top < stickyHeaderHeight + 10) {
              t.scrollIntoView({ behavior: "smooth", block: "start" });
              // Offset for sticky header
              setTimeout(() => {
                window.scrollBy({ top: -(stickyHeaderHeight + 20), behavior: "instant" });
              }, 300);
            }
          }, 500);
        }
      }
      
      n.emit("accordion:toggle", null, {
        open: willOpen,
        node: t,
        group: o,
        reset: true,
      });
    });

    const timerDuration = parseInt(a);
    let timerInterval = null;
    let progress = 0;

    n.on("accordion:toggle", (state, data = {}) => {
      if (o !== data.group) return;
      
      if (data.open) updateHeight();
      
      t.classList[data.open && data.node === t ? "add" : "remove"](openClass);

      // Timer logic
      if (a) {
        const { progress: progressEl } = e(t, null);
        const isCurrentlyOpen = t.classList.contains(openClass);
        
        if (data.reset) progress = 0;
        if (progressEl) progressEl.style.setProperty("width", `${progress}%`);

        if (isCurrentlyOpen) {
          timerInterval = setInterval(() => {
            progress += (100 / timerDuration) * 50;
            if (progressEl) progressEl.style.setProperty("width", `${progress}%`);
            
            if (progress >= 100) {
              clearInterval(timerInterval);
              progress = 0;
              
              const nextAccordion = timerAccordions.find(
                (el) => Number(el.dataset.index) === Number(r) + 1
              ) || timerAccordions[0];
              
              n.emit("accordion:toggle", null, {
                open: true,
                node: nextAccordion,
                group: o,
              });
            }
            
            if (!isInView()) clearInterval(timerInterval);
          }, 50);
        } else {
          clearInterval(timerInterval);
        }
      }

      // Toggle button text
      if (i.dataset.toggleMore && i.dataset.toggleLess) {
        isOpen = !isOpen;
        i.innerHTML = isOpen ? i.dataset.toggleLess : i.dataset.toggleMore;
      }
    });

    // Auto-start timer for first accordion in group
    if (a && r === "0" && o) {
      t.setAttribute("data-in-view", false);
      
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            t.setAttribute("data-in-view", entry.isIntersecting);
            if (isInView()) {
              const openAccordion = timerAccordions.find((el) => el.classList.contains(openClass));
              n.emit("accordion:toggle", null, {
                open: true,
                node: openAccordion,
                group: o,
              });
            }
          });
        },
        { root: null, rootMargin: "200px", threshold: 0.5 }
      );
      
      observer.observe(t.parentNode || t);
      
      return () => {
        observer.disconnect();
        clearInterval(timerInterval);
      };
    }
    
    return () => clearInterval(timerInterval);
  });

  const p = window.component((t, n) => {
    const {
      cartCount: o,
      cartToggle: a,
      mobileMenuToggle: r,
      collectionNavLi: s,
      navigationLink: i,
      menuCloseButton: c,
      searchToggle: headerSearchToggle,
    } = e(t, null);
    
    if (!t) return; // Defensive check

    const searchToggles = [].concat(headerSearchToggle).filter(Boolean);
    const { scrollY: initialScrollY } = window;
    
    const isTransparent = t.classList.contains("bg-transparent") || 
                          t.classList.contains("lg:bg-transparent");

    // Initial header state
    if (isTransparent) {
      t.classList[initialScrollY >= SCROLL_THRESHOLD ? "add" : "remove"]("!bg-white");
      t.classList[initialScrollY >= SCROLL_THRESHOLD ? "add" : "remove"]("!text-black");
      if (t.parentElement) {
        t.parentElement.style.top = TOP_OFFSET - (initialScrollY >= TOP_OFFSET ? TOP_OFFSET : initialScrollY) + "px";
      }
    }

    const updateCartCount = (cart) => {
      if (cart?.token && o) {
        const count = cart.item_count;
        o.textContent = count > 0 ? count : "";

        // Sale badge: swap classes/style that liquid only renders on page load
        const activeClasses = (o.dataset.countActiveClass || "").split(" ").filter(Boolean);
        const inactiveClasses = (o.dataset.countInactiveClass || "").split(" ").filter(Boolean);
        if (activeClasses.length || inactiveClasses.length) {
          o.classList.remove(...(count > 0 ? inactiveClasses : activeClasses));
          o.classList.add(...(count > 0 ? activeClasses : inactiveClasses));
          if (count > 0) {
            o.setAttribute("style", o.dataset.countActiveStyle || "");
          } else {
            o.removeAttribute("style");
          }
        }
      }
    };

    // Throttled scroll handler
    const handleScroll = throttleRAF(() => {
      const { scrollY } = window;
      
      if (isTransparent) {
        if (t.parentElement) {
          t.parentElement.style.top = TOP_OFFSET - (scrollY >= TOP_OFFSET ? TOP_OFFSET : scrollY) + "px";
        }
        t.classList[scrollY >= SCROLL_THRESHOLD ? "add" : "remove"]("!bg-white");
        t.classList[scrollY >= SCROLL_THRESHOLD ? "add" : "remove"]("!text-black");
      }

      // Update nav link borders
      const updateNavLinks = (links) => {
        if (!links) return;
        [].concat(links).forEach((link) => {
          if (!link) return;
          link.classList[scrollY >= SCROLL_THRESHOLD ? "add" : "remove"]("after:border-blue-900");
          link.classList[scrollY >= SCROLL_THRESHOLD ? "remove" : "add"]("after:border-white");
        });
      };
      
      updateNavLinks(s);
      updateNavLinks(i);
    });

    // Use bindOnce for scroll handler
    bindOnce(document, "header-scroll", "scroll", handleScroll, { passive: true });

    n.on("searchBar:toggle", ({ searchBarOpen }) => {
      const { scrollY } = window;
      
      if (isTransparent && scrollY < SCROLL_THRESHOLD) {
        const updateNavLinks = (links, isOpen) => {
          if (!links) return;
          [].concat(links).forEach((link) => {
            if (!link) return;
            link.classList[isOpen ? "add" : "remove"]("after:border-blue-900");
            link.classList[isOpen ? "remove" : "add"]("after:border-white");
          });
        };
        updateNavLinks(s, searchBarOpen);
        updateNavLinks(i, searchBarOpen);
      }
      
      searchToggles.forEach((toggle) => {
        toggle.classList[searchBarOpen ? "add" : "remove"]("open");
      });
    });

    n.on("cart:updated", ({ cart }) => updateCartCount(cart));
    n.on("toggle:mobile-menu", ({ smMenuOpen }) => {
      t.classList[smMenuOpen ? "add" : "remove"]("open");
    });

    if (a) {
      a.addEventListener("click", (ev) => {
        ev.preventDefault();
        if (!window.location.href.includes(window.Stox?.routes?.cart || '/cart')) {
          n.emit("cart:toggle", ({ cartOpen }) => ({ cartOpen: !cartOpen ?? true }));
        }
      });
    }

    updateCartCount(n.getState()?.cart);

    if (r) {
      r.addEventListener("click", (ev) => {
        ev.preventDefault();
        n.emit("toggle:mobile-menu", ({ smMenuOpen }) => ({ smMenuOpen: !smMenuOpen ?? true }));
      });
    }

    if (c) {
      c.addEventListener("click", (ev) => {
        ev.preventDefault();
        n.emit("toggle:mobile-menu", () => ({ smMenuOpen: false }));
      });
    }
    
    return () => {
      unbind(document, "header-scroll", "scroll", handleScroll, { passive: true });
    };
  });

  const g = {
    sectionRendering: s,
    consoleLog: i,
    accordion: c,
    header: p,
    
    cartDrawer: window.component(async (t, n) => {
      const o = t.parentNode;
      const { clear: clearBtn, focusEl: focus, addGiftWrapping: giftBtn } = e(t, null);
      let cartDrawerCheckoutRefreshed = false;
      
      if (!t) return; // Defensive check

      const handleCartToggle = ({ cartOpen }) => {
        n.emit("body:toggleScroll", () => ({ enable: cartOpen ?? true }));
        t.classList[cartOpen ? "add" : "remove"]("cart-open");
        
        const header = document.querySelector("[data-header]");
        if (header) {
          header.classList[cartOpen ? "add" : "remove"]("cart-open");
        }
        
        if (cartOpen) {
          if (!cartDrawerCheckoutRefreshed && !isCartPage) {
            cartDrawerCheckoutRefreshed = true;
            fetchCartDrawerSection()
              .then((cart) => {
                if (cart.item_count > 0) {
                  window.app?.emit?.(["cart:updated", "cart:render"], { cart });
                }
              })
              .catch((err) => {
                console.warn("Failed to refresh cart drawer accelerated checkout:", err);
              });
          }

          t.tabIndex = "0";
          (focus || t).focus();
          updateExpressCheckout(t);
        } else {
          t.tabIndex = "-1";
        }
      };

      handleCartToggle(n.getState());

      if (clearBtn) {
        clearBtn.addEventListener("click", clearCart);
      }

      const handleEscape = (ev) => handleEscapeKey(ev, { type: "cart:toggle", boolean: "cartOpen" });
      t.addEventListener("keyup", handleEscape);

      const handleClick = (ev) => {
        if (Object.keys(ev.target?.dataset || {}).includes("toggle")) {
          n.emit("cart:toggle", ({ cartOpen }) => ({ cartOpen: !cartOpen ?? true }));
        }
        const upsellBtn = ev.target.closest?.("[data-add-upsell]");
        if (upsellBtn) {
          const variantId = parseInt(upsellBtn.dataset.id, 10);
          if (variantId) addToCart([{
            id: variantId,
            quantity: 1,
            properties: {
              _upsell_discount: "true",
              _upsell_source: upsellBtn.dataset.upsellSource || "",
              _upsell_gender: upsellBtn.dataset.upsellGender || "",
            },
          }]);
          const upsellModule = t.querySelector("[data-upsell-module]");
          if (upsellModule && typeof gtag !== "undefined") {
            const pd = upsellProductData(upsellModule);
            gtag("event", "upsell_add_to_cart", {
              upsell_product_id: pd.item_id,
              upsell_product_name: pd.item_name,
              upsell_variant_id: pd.item_variant,
              upsell_original_price: pd.price,
              upsell_discount_amount: pd.discount,
              upsell_discounted_price: (pd.price - pd.discount),
              upsell_gender: pd.item_category2,
              upsell_source: upsellBtn.dataset.upsellSource || upsellModule.dataset.upsellSource || "",
              currency: upsellModule.dataset.upsellCurrency || "EUR",
            });
          }
        }
      };
      t.addEventListener("click", handleClick);

      function upsellProductData(el) {
        return {
          item_id: el.dataset.upsellProductId,
          item_name: el.dataset.upsellProductTitle,
          item_variant: el.dataset.upsellVariantId,
          price: parseFloat(el.dataset.upsellPrice) || 0,
          discount: (parseFloat(el.dataset.upsellPrice) || 0) - (parseFloat(el.dataset.upsellDiscountPrice) || 0),
          item_category: "Recovery Socks",
          item_category2: el.dataset.upsellGender,
          promotion_name: "Cart Upsell",
          quantity: 1,
        };
      }

      let upsellViewTracked = false;
      let upsellObserver = null;
      function observeUpsellModule() {
        if (upsellObserver) upsellObserver.disconnect();
        const upsellModule = t.querySelector("[data-upsell-module]");
        if (!upsellModule || typeof gtag === "undefined") return;
        upsellObserver = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting && !upsellViewTracked) {
              upsellViewTracked = true;
              const pd = upsellProductData(upsellModule);
              gtag("event", "upsell_shown", {
                upsell_product_id: pd.item_id,
                upsell_product_name: pd.item_name,
                upsell_variant_id: pd.item_variant,
                upsell_original_price: pd.price,
                upsell_discount_amount: pd.discount,
                upsell_discounted_price: (pd.price - pd.discount),
                upsell_gender: pd.item_category2,
                upsell_source: upsellModule.dataset.upsellSource || "",
              });
              upsellObserver.disconnect();
            }
          }
        }, { threshold: 0.5 });
        upsellObserver.observe(upsellModule);
      }
      observeUpsellModule();

      n.on("cart:render", ({ cart }) => {
        if (!o || !cart?.sections?.["cart-drawer"]) return;
        
        const newDoc = new DOMParser().parseFromString(cart.sections["cart-drawer"], "text/html");
        const newInner = newDoc.getElementById("cart-inner");
        
        if (newInner) {
          t.innerHTML = newInner.innerHTML;
          // Only mount new components within the cart drawer, don't unmount globally
          // as that would destroy event handlers for other components (mobile menu, accordions, etc.)
          window.app?.mount?.();
          observeUpsellModule();
          updateExpressCheckout(t);
        }
      });

      n.on("cart:toggle", handleCartToggle);

      if (giftBtn) {
        giftBtn.addEventListener("click", () => {
          addToCart([{ id: parseInt(giftBtn.dataset.id, 10), quantity: 1 }]);
        });
      }

      return () => {
        t.removeEventListener("keyup", handleEscape);
        t.removeEventListener("click", handleClick);
      };
    }),

    cartItem: window.component((t) => {
      const { update: updateBtns } = e(t, null);
      const { key } = t.dataset;
      
      if (!key || !updateBtns) return; // Defensive check

      [].concat(updateBtns).forEach((btn) => {
        const { value } = btn.dataset;
        btn.addEventListener("click", () => updateCartItem(key, value));
      });
    }),

    mobileMenu: window.component(async (t, n) => {
      const { mobileMenuBody: o, menuCloseButton: a, topNavItem: r } = e(t, null);
      
      if (!t) return; // Defensive check
      
      const viewportHeight = Math.max(
        document.documentElement.clientHeight || 0,
        window.innerHeight || 0
      );
      let searchBarOpen = false;
      
      const topNavItems = [].concat(r).filter(Boolean);
      topNavItems.forEach((item) => {
        item.addEventListener("click", () => {
          topNavItems.forEach((i) => i.classList.remove("open"));
          item.classList.add("open");
        });
      });

      n.on("toggle:mobile-menu", ({ smMenuOpen }) => {
        const { scrollY } = window;
        t.style.top = SCROLL_THRESHOLD - (scrollY >= TOP_OFFSET ? TOP_OFFSET : scrollY) + "px";
        t.classList[smMenuOpen ? "add" : "remove"]("open");
        
        if (smMenuOpen && searchBarOpen) {
          n.emit("searchBar:toggle", () => ({ searchBarOpen: false }));
        }
        
        n.emit("body:toggleScroll", () => ({ enable: smMenuOpen ?? false }));
        
        if (o) {
          const menuHeight = viewportHeight - (SCROLL_THRESHOLD - (scrollY >= TOP_OFFSET ? TOP_OFFSET : scrollY));
          o.style.height = smMenuOpen && menuHeight >= o.clientHeight ? "100%" : "auto";
        }
      });

      n.on("searchBar:toggle", ({ searchBarOpen: isOpen }) => {
        searchBarOpen = isOpen;
      });

      if (a) {
        [].concat(a).filter(Boolean).forEach((btn) => {
          btn.addEventListener("click", (ev) => {
            ev.preventDefault();
            n.emit("toggle:mobile-menu", () => ({ smMenuOpen: false }));
          });
        });
      }
    }),

    cartPage: window.component((t, n) => {
      const { addGiftWrapping: giftBtn } = e(t, null);
      
      if (giftBtn) {
        giftBtn.addEventListener("click", () => {
          addToCart([{ id: parseInt(giftBtn.dataset.id, 10), quantity: 1 }]);
        });
      }

      const parent = t.parentNode;

      updateExpressCheckout(t);

      n.on("cart:render", ({ cart }) => {
        if (!parent || !cart?.sections?.["main-cart"]) return;
        
        const section = document.getElementById("shopify-section-main-cart");
        if (section) {
          section.outerHTML = cart.sections["main-cart"];
          // Only mount new components within the cart section, don't unmount globally
          // as that would destroy event handlers for other components (mobile menu, accordions, etc.)
          window.app?.mount?.();
          updateExpressCheckout(document);
        }
      });
    }),

    toggleBodyScroll: window.component((body, n) => {
      let savedScrollY = 0;
      
      n.on("body:toggleScroll", ({ smMenuOpen, searchBarOpen, cartOpen, filters, sizeSelectorOpen }) => {
        const shouldLock = smMenuOpen || searchBarOpen || cartOpen || filters || sizeSelectorOpen;
        
        if (shouldLock) {
          savedScrollY = window.scrollY;
          body.classList.add("overflow-hidden");
          
          if (window.innerWidth > 1024) {
            // Calculate scrollbar width
            const scrollDiv = document.createElement("div");
            scrollDiv.className = "scrollbar-measure";
            document.body.appendChild(scrollDiv);
            const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
            document.body.removeChild(scrollDiv);
            document.body.style.paddingRight = `${scrollbarWidth}px`;
          }
        } else {
          if (window.innerWidth > 1024) {
            document.body.style.removeProperty("padding-right");
          }
          body.classList.remove("overflow-hidden");
          window.scrollTo(0, savedScrollY);
        }
      });
    }),

    languageForm: window.component((t) => {
      const { select: sel } = e(t, null);
      if (sel) {
        sel.addEventListener("change", () => t.submit());
      }
    }),

    search: window.component((t, n) => {
      const { searchToggle: toggles } = e(t, null);
      if (!t) return; // Defensive check
      
      const searchToggles = [].concat(toggles).filter(Boolean);
      let mobileMenuOpen = false;

      n.on("searchBar:toggle", ({ searchBarOpen }) => {
        n.emit("body:toggleScroll", () => ({ enable: searchBarOpen ?? false }));
        t.classList[searchBarOpen ? "add" : "remove"]("search-open");
        
        if (searchBarOpen) {
          const { scrollY } = window;
          const topPosition = window.innerWidth > 1024 
            ? 102 - (scrollY >= TOP_OFFSET ? TOP_OFFSET : scrollY)
            : SCROLL_THRESHOLD - (scrollY >= TOP_OFFSET ? TOP_OFFSET : scrollY);
          t.style.top = topPosition + "px";
          t.tabIndex = "0";
          
          // Focus the search input directly by ID to ensure it always works
          // regardless of which element this component is mounted on
          const searchInput = document.getElementById("search");
          if (searchInput) {
            // Small delay to ensure the search overlay is visible before focusing
            requestAnimationFrame(() => searchInput.focus());
          }
        } else {
          t.style.top = 0;
          t.tabIndex = "-1";
        }
        
        if (mobileMenuOpen && searchBarOpen) {
          n.emit("toggle:mobile-menu", () => ({ smMenuOpen: false }));
        }
      });

      n.on("toggle:mobile-menu", ({ smMenuOpen }) => {
        mobileMenuOpen = smMenuOpen;
      });

      searchToggles.forEach((toggle) => {
        toggle.addEventListener("click", () => {
          n.emit("searchBar:toggle", ({ searchBarOpen }) => ({ searchBarOpen: !searchBarOpen ?? true }));
        });
      });

      const handleEscape = (ev) => handleEscapeKey(ev, { type: "searchBar:toggle", boolean: "searchBarOpen" });
      t.addEventListener("keyup", handleEscape);
      
      return () => t.removeEventListener("keyup", handleEscape);
    }),

    predictiveSearch: window.component((t) => {
      const {
        predictiveSearch: container,
        focusEl: focus,
        dummyCopy,
        dummyComplete,
        dummyPusher,
        searchInput,
      } = e(t, null);
      
      if (!container || !searchInput) return; // Defensive check

      let autocompleteTerm = "";
      let abortController;
      let isEmpty = false;

      const hasWhitespace = (str) => /\s/.test(str);
      
      const clearAutocomplete = () => {
        if (dummyCopy) dummyCopy.textContent = "";
        if (dummyComplete) dummyComplete.textContent = "";
        if (dummyPusher) dummyPusher.textContent = "";
        autocompleteTerm = "";
      };

      const toggleResults = (show) => {
        if (focus) focus.classList[show ? "add" : "remove"]("open");
        container.classList[show ? "remove" : "add"]("hidden");
        container.classList[show ? "add" : "remove"]("block");
        window.app?.mount?.();
      };

      const getLocale = () => {
        const path = window.location.pathname?.split("/")[1]?.toLowerCase();
        const localeMap = { "en-gb": "en-GB", nl: "nl", de: "de-DE", "de-de": "de-DE" };
        return localeMap[path] || "en";
      };

      const fetchResults = (query) => {
        abortController = new AbortController();
        const locale = getLocale();
        const basePath = locale === "en" ? "/" : `/${locale}/`;
        
        fetch(
          `${basePath}search/suggest?q=${query}&resources[type]=product,page,article&resources[limit]=10&resources[options][unavailable_products]=show&resources[options][fields]=title,product_type,variants.title,variants.sku,body,tag&section_id=predictive-search`,
          { signal: abortController.signal }
        )
          .then((res) => {
            if (!res.ok) throw new Error(res.status);
            return res.text();
          })
          .then((html) => {
            const newContent = new DOMParser()
              .parseFromString(html, "text/html")
              .querySelector("#shopify-section-predictive-search");
            
            if (newContent) {
              container.innerHTML = newContent.innerHTML;
              toggleResults(true);
            }

            // Autocomplete logic
            if (!isEmpty && !hasWhitespace(query) && query !== "") {
              const { productTitle } = e(t, null);
              const titles = [].concat(productTitle);
              const firstMatch = titles[0]?.textContent
                ?.toLowerCase()
                ?.split(" ")
                ?.find((word) => word.startsWith(query.toLowerCase()));

              if (!firstMatch) {
                clearAutocomplete();
                return;
              }

              if (autocompleteTerm !== firstMatch) autocompleteTerm = firstMatch;
              if (dummyCopy && dummyCopy.textContent !== query) dummyCopy.textContent = query;
              
              const completion = firstMatch.slice(query.length);
              if (dummyComplete && dummyComplete.textContent !== completion) dummyComplete.textContent = completion;
              if (dummyPusher) dummyPusher.textContent = completion;
            }
          })
          .catch(() => toggleResults(false))
          .finally(() => {
            if (isEmpty) {
              clearAutocomplete();
              toggleResults(false);
            }
          });
      };

      const debouncedFetch = debounce((ev) => {
        const query = ev.target.value;
        if (query.length > 0) {
          fetchResults(query);
        } else {
          toggleResults(false);
        }
      }, 100);

      searchInput.addEventListener("input", debouncedFetch);

      searchInput.addEventListener("input", () => {
        const value = searchInput.value;
        
        if (value.length === 0) {
          isEmpty = true;
          if (abortController) abortController.abort();
        } else {
          isEmpty = false;
        }

        if (value === "" || hasWhitespace(value) || !autocompleteTerm) {
          clearAutocomplete();
          return;
        }

        if (value.toLowerCase() === autocompleteTerm.slice(0, value.length)) {
          if (dummyCopy) dummyCopy.textContent = value;
          const completion = autocompleteTerm.slice(value.length);
          if (dummyComplete) dummyComplete.textContent = completion;
          if (dummyPusher) dummyPusher.textContent = completion;
        } else {
          clearAutocomplete();
        }
      });
    }),

    checkout: window.component((e, t) => {}),

    countryForm: window.component((t) => {
      const { select: sel } = e(t, null);
      if (sel) {
        sel.addEventListener("change", () => t.submit());
      }
    }),

    productItemThumbnail: window.component((t) => {
      const { colors: mobileColors, desktopColors } = e(t, null);
      if (!mobileColors && !desktopColors) return; // Defensive check
      
      const selectedClass = "product-thumbnail-color-options_selected";
      // Use desktopColors for both since template only has data-desktop-colors on <a> elements
      const colorSwatches = desktopColors || mobileColors;

      // Append a width parameter to a Shopify CDN image URL
      const withWidth = (url, width) => `${url}${url.includes("?") ? "&" : "?"}width=${width}`;
      const toSrcset = (url, widths) => widths.map((w) => `${withWidth(url, w)} ${w}w`).join(", ");

      // Swap the <img> and <picture><source> of a media wrapper to a new image,
      // keeping the responsive srcset widths from media-product-item.liquid.
      const applyImage = (wrapper, url) => {
        if (!wrapper || !url) return;
        wrapper.querySelectorAll("img").forEach((img) => {
          img.src = withWidth(url, 480);
          img.srcset = toSrcset(url, [360, 480, 720]);
        });
        wrapper.querySelectorAll("source").forEach((source) => {
          source.srcset = toSrcset(url, [960, 1200, 1600]);
        });
      };

      const updateProduct = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();

        const swatch = ev.currentTarget;
        const card = swatch.closest("[data-product-item-thumbnail]");
        if (!card) return;

        const links = card.querySelectorAll("[data-link]");

        // Main + hover image
        const imageUrl = swatch.getAttribute("data-image-url");
        const hoverImageUrl = swatch.getAttribute("data-hover-image-url");
        applyImage(card.querySelector("[data-variant-image]"), imageUrl);
        applyImage(card.querySelector("[data-hover-image]"), hoverImageUrl);

        // Warm up the new hover image so it shows instantly on hover
        if (hoverImageUrl) {
          new Image().src = withWidth(hoverImageUrl, 480);
        }

        // Price + compare-at (sale) price, pre-rendered per swatch in
        // product.thumbnail-color-images.liquid
        const priceWrapper = card.querySelector("[data-price-wrapper]");
        const priceTemplate = swatch.querySelector("template[data-swatch-price]");
        if (priceWrapper && priceTemplate) {
          priceWrapper.innerHTML = priceTemplate.innerHTML;
        }

        // Discount percentage badge
        const discountBadge = card.querySelector("[data-discount-badge]");
        if (discountBadge) {
          const discount = parseInt(swatch.getAttribute("data-discount-percentage"), 10) || 0;
          if (discount > 0) {
            discountBadge.textContent = `-${discount}%`;
            discountBadge.style.display = "";
          } else {
            discountBadge.style.display = "none";
          }
        }

        // Product label badge (e.g. "New" or the sale label from theme
        // settings), pre-rendered per swatch in product.thumbnail-color-images.liquid
        const labelBadge = card.querySelector("[data-label-badge]");
        if (labelBadge) {
          const labelText = swatch.getAttribute("data-label-text") || "";
          labelBadge.textContent = labelText;
          labelBadge.setAttribute("data-product-label", labelText);
          if (labelText) {
            labelBadge.style.backgroundColor = swatch.getAttribute("data-label-background-color") || "";
            labelBadge.style.color = swatch.getAttribute("data-label-text-color") || "";
            labelBadge.style.borderColor = swatch.getAttribute("data-label-border-color") || "";
            labelBadge.style.display = "";
          } else {
            labelBadge.style.display = "none";
          }
        }

        // Update selected state
        const parent = swatch.parentNode;
        if (parent) {
          // Always use "A" since we're using desktopColors (anchor elements) for both mobile and desktop
          const tagName = "A";
          let sibling = parent.firstChild;
          
          while (sibling) {
            if (sibling.nodeType === 1 && sibling !== swatch && sibling.tagName === tagName) {
              const inner = sibling.firstElementChild;
              if (inner?.classList.contains(selectedClass)) {
                inner.classList.remove(selectedClass);
                const hexcode = inner.getAttribute("data-hexcode");
                if (hexcode) inner.style.backgroundColor = hexcode;
              }
            }
            sibling = sibling.nextSibling;
          }
        }

        const swatchInner = swatch.firstElementChild;
        if (swatchInner && !swatchInner.classList.contains(selectedClass)) {
          swatchInner.classList.add(selectedClass);
          swatchInner.style.backgroundColor = "";
        }

        // Update all product links
        const productUrl = swatch.getAttribute("data-product-url");
        links.forEach((link) => {
          if (productUrl) link.setAttribute("href", productUrl);
        });
      };

      [].concat(colorSwatches).filter(Boolean).forEach((swatch) => {
        let touchMoved = false;

        swatch.addEventListener("click", updateProduct);
        swatch.addEventListener("touchstart", () => { touchMoved = false; }, { passive: true });
        swatch.addEventListener("touchmove", () => { touchMoved = true; }, { passive: true });
        swatch.addEventListener("touchend", (ev) => {
          if (!touchMoved) updateProduct(ev);
        }, { passive: false });
      });
    }),

    imageScrollParallax: window.component((el) => {
      if (!el) return; // Defensive check
      
      const images = el.querySelectorAll("[data-image]");
      if (!images.length) return;

      const handleScroll = throttleRAF(() => {
        const elTop = el.getBoundingClientRect().top;
        images.forEach((img) => {
          const speed = parseFloat(img.getAttribute("data-speed")) || 0;
          img.style.transform = `translateY(${elTop * speed * 0.1}px)`;
        });
      });

      bindOnce(window, `parallax-${el.id || Math.random()}`, "scroll", handleScroll, { passive: true });
    }),
  };

  // Register components
  window.app?.add?.(r);
  window.app?.add?.(g);
  window.app?.mount?.();

  // ==========================================================================
  // STANDALONE INITIALIZERS (idempotent, run once)
  // ==========================================================================

  // Track initialization state to prevent duplicate runs
  if (!window.__baseJsInitialized) {
    window.__baseJsInitialized = true;

    // --------------------------------------------------------------------------
    // Re-mount components after dynamic PLP updates
    // --------------------------------------------------------------------------
    // Facet filtering (facets.js) replaces the product grid HTML and infinite
    // scroll (collection-infinite-scroll.js) appends new product cards. Both
    // insert fresh [data-component] nodes that need to be mounted, otherwise
    // the color swatches on those cards have no click handlers. mount() is
    // idempotent: it removes the data-component attribute from nodes it
    // processes, so already-mounted cards are skipped.
    document.addEventListener("facets:updated", () => window.app?.mount?.());
    window.addEventListener("infiniteScroll:loaded", () => window.app?.mount?.());

    // --------------------------------------------------------------------------
    // Cart Refresh on Back/Forward Navigation
    // --------------------------------------------------------------------------
    // When the user navigates away (e.g. to Shopify checkout) and presses the
    // browser back button, the cart UI can be stale or empty because:
    //   1. bfcache restore: DOM is frozen from before checkout, cart state is stale
    //   2. HTTP cache: browser serves the cached HTML from before add-to-cart
    // We detect both scenarios and refetch cart data + section HTML so the
    // cart drawer/page re-renders correctly.
    window.addEventListener('pageshow', (event) => {
      const navEntry = performance.getEntriesByType?.("navigation")?.[0];
      const isBackForward = event.persisted ||
        navEntry?.type === "back_forward" ||
        performance.navigation?.type === 2;

      if (isBackForward) {
        const sectionId = isCartPage ? "main-cart" : "cart-drawer";
        Promise.all([
          fetch(`${window.Stox?.routes?.cart || '/cart'}.js`, {
            method: 'GET',
            credentials: 'include',
            headers: { 'Cache-Control': 'no-cache' }
          }).then((r) => r.json()),
          fetch(`${window.Stox?.routes?.cart || '/cart'}?sections=${sectionId}`, {
            method: 'GET',
            credentials: 'include',
            headers: { 'Cache-Control': 'no-cache' }
          }).then((r) => r.json()),
        ])
          .then(([cart, sections]) => {
            cart.sections = sections;
            window.app?.emit?.(['cart:updated', 'cart:render'], { cart });
            if (window.app?.hydrate) {
              window.app.hydrate({ cart });
            }
          })
          .catch((err) => {
            console.warn('Failed to refresh cart after back/forward navigation:', err);
          });
      }
    });

    // --------------------------------------------------------------------------
    // Lazy Video Loading
    // --------------------------------------------------------------------------
    onReady(() => {
      const lazyVideos = document.querySelectorAll("video.lazy");
      if (!lazyVideos.length || !("IntersectionObserver" in window)) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const video = entry.target;
            Array.from(video.children).forEach((child) => {
              if (child.tagName === "SOURCE" && child.dataset.src) {
                child.src = child.dataset.src;
              }
            });
            video.load();
            video.classList.remove("lazy");
            observer.unobserve(video);
          }
        });
      });

      lazyVideos.forEach((video) => observer.observe(video));
    });

    // --------------------------------------------------------------------------
    // Details/Summary Polyfill
    // --------------------------------------------------------------------------
    onReady(() => {
      const supportsDetails = "open" in document.createElement("details");
      if (supportsDetails) return;

      const details = document.querySelector(".description-toggle");
      const summary = document.querySelector(".read-more-toggle");
      const fullText = details?.querySelector("p");

      if (!details || !summary || !fullText) return;

      details.classList.add("no-details");
      fullText.style.display = "none";

      summary.addEventListener("click", (ev) => {
        ev.preventDefault();
        const isHidden = fullText.style.display === "none";
        fullText.style.display = isHidden ? "block" : "none";
        summary.style.display = isHidden ? "none" : "block";
      });
    });

    // --------------------------------------------------------------------------
    // Details Accordion (mutual exclusivity)
    // --------------------------------------------------------------------------
    document.querySelectorAll("details[name]").forEach((details) => {
      details.addEventListener("toggle", (ev) => {
        const name = details.getAttribute("name");
        if (ev.newState === "open") {
          document.querySelectorAll(`details[name="${name}"][open]`).forEach((other) => {
            if (other !== details) other.removeAttribute("open");
          });
        }
      });
    });

    // --------------------------------------------------------------------------
    // Scroll Container Wrapper (collection swatches)
    // --------------------------------------------------------------------------
    onReady(() => {
      document.querySelectorAll(".scroll-container-wrapper").forEach((wrapper) => {
        const container = wrapper.querySelector(".scroll-container-slider");
        const scrollWrapper = container?.querySelector(".image_swatches");
        const leftBtn = wrapper.querySelector(".scroll-left");
        const rightBtn = wrapper.querySelector(".scroll-right");

        if (!container || !scrollWrapper || !leftBtn || !rightBtn) return;

        const isOverflowing = () => scrollWrapper.scrollWidth > container.clientWidth;

        const updateVisibility = () => {
          if (!isOverflowing()) {
            leftBtn.style.display = "none";
            rightBtn.style.display = "none";
            return;
          }
          const maxScroll = container.scrollWidth - container.clientWidth;
          leftBtn.style.display = container.scrollLeft > 0 ? "block" : "none";
          rightBtn.style.display = container.scrollLeft < maxScroll ? "block" : "none";
        };

        const scrollBy = (dir) => {
          container.scrollBy({ left: dir * 100, behavior: "smooth" });
          setTimeout(updateVisibility, 300);
        };

        leftBtn.addEventListener("click", () => scrollBy(-1));
        rightBtn.addEventListener("click", () => scrollBy(1));
        container.addEventListener("scroll", () => requestAnimationFrame(updateVisibility), { passive: true });

        updateVisibility();
      });
    });

    // --------------------------------------------------------------------------
    // Search Tracking (GTM)
    // --------------------------------------------------------------------------
    onReady(() => {
      const searchInput = document.querySelector("[data-search-input]");
      if (!searchInput) return;

      let timeout = null;

      searchInput.addEventListener("input", (ev) => {
        clearTimeout(timeout);
        const searchTerm = ev.target.value.trim();

        if (searchTerm.length > 2) {
          timeout = setTimeout(() => {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ event: "search", search_term: searchTerm });

            // Send to Shopify Search & Discovery
            fetch(`/search?q=${encodeURIComponent(searchTerm)}&view=log`, {
              method: "GET",
              headers: { "Content-Type": "application/json" },
            }).catch(() => {}); // Ignore errors
          }, 1000);
        }
      });
    });

    // --------------------------------------------------------------------------
    // Product Recommendations
    // --------------------------------------------------------------------------
    (() => {
      const section = document.querySelector(".product-recommendations");
      if (!section) return;

      const loadRecommendations = () => {
        const url = section.dataset.url;
        if (!url) return;

        fetch(url)
          .then((res) => res.text())
          .then((text) => {
            const html = document.createElement("div");
            html.innerHTML = text;
            const recommendations = html.querySelector(".product-recommendations");
            if (recommendations?.innerHTML.trim().length) {
              section.innerHTML = recommendations.innerHTML;
            }
          })
          .catch(console.error);
      };

      if ("requestIdleCallback" in window) {
        requestIdleCallback(loadRecommendations, { timeout: 2000 });
      } else {
        setTimeout(loadRecommendations, 1000);
      }
    })();

    // --------------------------------------------------------------------------
    // CSS Slider Initialization
    // --------------------------------------------------------------------------
    onReady(() => {
      const initializeSliders = () => {
        document.querySelectorAll(".css-slider").forEach((list) => {
          if (list.classList.contains("is-initialized")) return;

          const wrapper = list.closest(".css-slider-wrapper");
          if (!wrapper) return;

          const slides = list.querySelectorAll(".css-slide");
          if (!slides.length) return;

          // Get ALL navigation buttons (both mobile and desktop)
          // This ensures clicks work regardless of viewport size
          const prevButtonMobile = wrapper.querySelector(".button--previous-mobile");
          const nextButtonMobile = wrapper.querySelector(".button--next-mobile");
          const prevButtonDesktop = wrapper.querySelector(".button--previous-desktop");
          const nextButtonDesktop = wrapper.querySelector(".button--next-desktop");
          const dotsContainer = wrapper.querySelector("[data-dots]");
          const progressBar = wrapper.querySelector(".slider-progress-bar");

          // Need at least one set of buttons to function
          const hasMobileButtons = prevButtonMobile && nextButtonMobile;
          const hasDesktopButtons = prevButtonDesktop && nextButtonDesktop;
          if (!hasMobileButtons && !hasDesktopButtons) return;

          const dots = dotsContainer ? [...dotsContainer.children] : [];
          const getItemWidth = () => slides[0]?.offsetWidth || 200;
          const getMaxScroll = () => list.scrollWidth - list.clientWidth;

          const updateArrows = () => {
            requestAnimationFrame(() => {
              const maxScrollLeft = getMaxScroll();
              const isAtStart = list.scrollLeft <= 1;
              const isAtEnd = list.scrollLeft >= maxScrollLeft - 1;
              
              // Update all buttons (CSS handles which are visible)
              if (prevButtonMobile) prevButtonMobile.classList.toggle("button-disabled", isAtStart);
              if (nextButtonMobile) nextButtonMobile.classList.toggle("button-disabled", isAtEnd);
              if (prevButtonDesktop) prevButtonDesktop.classList.toggle("button-disabled", isAtStart);
              if (nextButtonDesktop) nextButtonDesktop.classList.toggle("button-disabled", isAtEnd);
              
              if (dots.length) updateActiveDot();
              if (progressBar) updateProgressBar();
            });
          };

          const updateProgressBar = () => {
            const progress = ((list.scrollLeft + list.clientWidth) / list.scrollWidth) * 100;
            progressBar.style.width = `${Math.min(progress, 100)}%`;
          };

          const handleClick = (direction) => {
            const itemWidth = getItemWidth();
            list.scrollBy({
              left: direction === "previous" ? -itemWidth : itemWidth,
              behavior: "smooth",
            });
            setTimeout(updateArrows, 300);
          };

          const updateActiveDot = () => {
            const itemWidth = getItemWidth();
            const activeIndex = Math.round(list.scrollLeft / itemWidth);
            dots.forEach((dot, index) => dot.classList.toggle("open", index === activeIndex));
          };

          const handleDotClick = (index) => {
            const itemWidth = getItemWidth();
            list.scrollTo({ left: index * itemWidth, behavior: "smooth" });
            setTimeout(updateArrows, 300);
          };

          // Add click handlers to ALL buttons (CSS controls visibility)
          if (prevButtonMobile) prevButtonMobile.addEventListener("click", () => handleClick("previous"));
          if (nextButtonMobile) nextButtonMobile.addEventListener("click", () => handleClick("next"));
          if (prevButtonDesktop) prevButtonDesktop.addEventListener("click", () => handleClick("previous"));
          if (nextButtonDesktop) nextButtonDesktop.addEventListener("click", () => handleClick("next"));
          
          list.addEventListener("scroll", updateArrows, { passive: true });

          const debouncedResize = debounce(updateArrows, 200);
          window.addEventListener("resize", debouncedResize, { passive: true });

          dots.forEach((dot, index) => dot.addEventListener("click", () => handleDotClick(index)));

          list.classList.add("is-initialized");
          updateArrows();
        });
      };

      initializeSliders();

      // Observe for dynamic content (product recommendations, etc.)
      const observer = new MutationObserver(debounce(initializeSliders, 300));
      observer.observe(document.body, { childList: true, subtree: true });
    });

    // --------------------------------------------------------------------------
    // Product Card Hover Preload
    // --------------------------------------------------------------------------
    document.querySelectorAll("[data-product-item-thumbnail]").forEach((card) => {
      const link = card.querySelector("[data-link]");
      const hoverImg = card.querySelector("[data-hover-image] img");

      if (!hoverImg || !link) return;

      let preloaded = false;

      const preloadHover = () => {
        if (preloaded) return;
        const img = new Image();
        img.src = hoverImg.getAttribute("src");
        preloaded = true;
      };

      link.addEventListener("mouseenter", preloadHover);
      link.addEventListener("touchstart", preloadHover, { passive: true });
    });

  }
})();
